import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  FileText, Search, Loader2, CheckCircle2, Clock, AlertCircle, Building2, CreditCard,
  Wallet, Plane, UploadCloud, Save, XCircle, Eye,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { api, apiErrorMessage } from "@/lib/api";
import { RESERVA_FACTURACION_LABELS, RESERVA_TYPE_LABELS, fmtDate } from "@/pages/trips/shared";
import { FacturaIA } from "@/pages/trips/FacturaIA";

const ALL = "__all__";
const DEFAULT_PAGO = "pagada_sin_factura";
const FACT_ORDER = ["sin_factura", "factura", "factura_mensual", "facturado_mensual", "no_aplica"];

const PAGOS = {
  pagada_sin_factura: { label: "Pagada · sin factura", icon: AlertCircle, tile: "bg-rose-100 text-rose-700" },
  pagada_con_factura: { label: "Pagada · con factura", icon: CheckCircle2, tile: "bg-emerald-100 text-emerald-700" },
  no_pagada_sin_factura: { label: "No pagada · sin factura", icon: Clock, tile: "bg-amber-100 text-amber-700" },
  no_pagada_con_factura: { label: "No pagada · con factura", icon: FileText, tile: "bg-sky-100 text-sky-700" },
};
const PAGO_ORDER = ["pagada_sin_factura", "pagada_con_factura", "no_pagada_sin_factura", "no_pagada_con_factura"];

function PagoBadge({ pagada }) {
  return pagada
    ? <Badge variant="outline" className="gap-1 text-[10px] border-emerald-200 bg-emerald-50 text-emerald-700"><Wallet className="h-3 w-3" />Pagada</Badge>
    : <Badge variant="outline" className="gap-1 text-[10px] border-zinc-200 bg-zinc-50 text-zinc-600"><CreditCard className="h-3 w-3" />No pagada</Badge>;
}

function FacturaBadge({ r }) {
  if (r.recibida) return <Badge variant="outline" className="gap-1 text-[10px] border-emerald-200 bg-emerald-50 text-emerald-700"><CheckCircle2 className="h-3 w-3" />Con factura</Badge>;
  return <Badge variant="outline" className="gap-1 text-[10px] border-rose-200 bg-rose-50 text-rose-700"><XCircle className="h-3 w-3" />Sin factura</Badge>;
}

// Calcula base (sin IVA) y total (con IVA) a partir de las líneas extraídas por la IA
function totalsFromExtraction(ext) {
  const lineas = (ext && ext.lineas) || [];
  let base = 0, total = 0;
  for (const ln of lineas) {
    const p = parseFloat(ln.precio_unidad) || 0;
    const u = parseFloat(ln.unidades) || 1;
    const d = parseFloat(ln.descuento_pct) || 0;
    const i = parseFloat(ln.iva_pct) || 0;
    const sub = p * u * (1 - d / 100);
    base += sub;
    total += sub * (1 + i / 100);
  }
  const r2 = (n) => Math.round(n * 100) / 100;
  return { base: r2(base), total: r2(total) };
}

// ============= Diálogo: replica el bloque "Facturas" de la reserva =============
function SubirFacturaDialog({ row, onClose, onSaved }) {
  const [form, setForm] = useState({
    status_facturacion: row.status_facturacion || "sin_factura",
    invoice_tax_excl: row.invoice_tax_excl ?? "",
    invoice_tax_incl: row.invoice_tax_incl ?? "",
    proveedor_mensual: !!row.proveedor_mensual,
  });
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const num = (v) => (v === "" || v == null ? 0 : parseFloat(v) || 0);

  const saveMut = useMutation({
    mutationFn: async (override) => {
      const f = override || form;
      return (await api.put(`/trips/${row.trip_id}/reservas/${row.id}`, {
        status_facturacion: f.status_facturacion || "sin_factura",
        invoice_tax_excl: num(f.invoice_tax_excl),
        invoice_tax_incl: num(f.invoice_tax_incl),
        proveedor_mensual: !!f.proveedor_mensual,
      })).data;
    },
    onSuccess: (_, override) => { toast.success(override ? "Datos rellenados con la lectura IA y guardados" : "Datos de facturación guardados"); onSaved && onSaved(); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  // Al subir y leer la factura con IA: autocompleta estado + importes y guarda
  const onUploaded = (factura) => {
    if (!factura || factura.estado !== "extraida") return;
    const { base, total } = totalsFromExtraction(factura.extraccion);
    const next = {
      ...form,
      status_facturacion: "factura",
      invoice_tax_excl: base || form.invoice_tax_excl,
      invoice_tax_incl: total || form.invoice_tax_incl,
    };
    setForm(next);
    saveMut.mutate(next);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="sub-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <UploadCloud className="h-4 w-4 text-primary" />{row.service || "Reserva"}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {row.client_name} · {row.provider_name || "—"} · {[fmtDate(row.date_entry), row.city].filter(Boolean).join(" · ")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Card><CardContent className="grid grid-cols-1 gap-3 p-5 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Estado Facturación</Label>
              <Select value={form.status_facturacion || "sin_factura"} onValueChange={(v) => set("status_facturacion", v)}>
                <SelectTrigger data-testid="sub-estado"><SelectValue /></SelectTrigger>
                <SelectContent>{FACT_ORDER.map((k) => <SelectItem key={k} value={k}>{RESERVA_FACTURACION_LABELS[k]}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">Al subir el PDF, la IA rellena automáticamente el estado y los importes de abajo. Puedes corregirlos si hace falta.</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">F. sin IVA (coste real factura)</Label>
              <Input type="number" step="0.01" value={form.invoice_tax_excl} onChange={(e) => set("invoice_tax_excl", e.target.value)} data-testid="sub-inv-excl" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">F. con IVA (coste real factura)</Label>
              <Input type="number" step="0.01" value={form.invoice_tax_incl} onChange={(e) => set("invoice_tax_incl", e.target.value)} data-testid="sub-inv-incl" />
            </div>
            <div className="flex items-center justify-between rounded-md border px-3 py-2 sm:col-span-2">
              <Label className="text-xs font-medium">Proveedor mensual (facturación mensual)</Label>
              <Switch checked={!!form.proveedor_mensual} onCheckedChange={(v) => set("proveedor_mensual", v)} data-testid="sub-prov-mensual" />
            </div>
            <div className="sm:col-span-2 flex justify-end">
              <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending} data-testid="sub-save">
                {saveMut.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}Guardar datos
              </Button>
            </div>
          </CardContent></Card>

          <FacturaIA tripId={row.trip_id} reservaId={row.id} reservaType={RESERVA_TYPE_LABELS[row.type] || row.type} onUploaded={onUploaded} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function SubirFacturasTab() {
  const qc = useQueryClient();
  const [tripId, setTripId] = useState(ALL);
  const [pago, setPago] = useState(DEFAULT_PAGO);
  const [q, setQ] = useState("");
  const [openRow, setOpenRow] = useState(null);

  const tripsQ = useQuery({ queryKey: ["sf-trips"], queryFn: async () => (await api.get("/solicitud-factura/trips")).data });
  const trips = tripsQ.data?.items || [];

  const params = useMemo(() => ({
    trip_id: tripId === ALL ? undefined : tripId,
    pago: pago === ALL ? undefined : pago,
    q: q.trim() || undefined,
  }), [tripId, pago, q]);
  const listQ = useQuery({ queryKey: ["subir-list", params], queryFn: async () => (await api.get("/solicitud-factura/list", { params })).data });
  const rows = listQ.data?.reservas || [];
  const pagoSummary = listQ.data?.pago_summary || {};

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["subir-list"] });
    qc.invalidateQueries({ queryKey: ["sf-list"] });
    qc.invalidateQueries({ queryKey: ["por-estado"] });
  };

  return (
    <div className="space-y-4" data-testid="subir-facturas-tab">
      <p className="text-sm text-muted-foreground">
        Sube el PDF de la factura del proveedor directamente desde aquí, sin entrar en cada viaje. Por defecto verás las <strong>pagadas sin factura</strong> (las que faltan por subir).
      </p>

      {/* Matriz pago × factura (clicable) */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {PAGO_ORDER.map((k) => {
          const meta = PAGOS[k];
          const Icon = meta.icon;
          const active = pago === k;
          return (
            <button key={k} type="button" onClick={() => setPago(active ? ALL : k)}
              className={`flex items-center gap-3 rounded-xl border bg-card px-4 py-3 text-left transition-all hover:shadow-md ${active ? "border-primary ring-2 ring-primary/30" : ""}`}
              data-testid={`sub-tile-${k}`}>
              <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${meta.tile}`}><Icon className="h-4 w-4" /></div>
              <div className="min-w-0">
                <p className="text-[10px] uppercase leading-tight tracking-wide text-muted-foreground">{meta.label}</p>
                <p className="text-xl font-bold leading-none tabular-nums">{pagoSummary[k] ?? 0}</p>
              </div>
            </button>
          );
        })}
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 p-4">
          <div className="space-y-1">
            <Label className="text-xs flex items-center gap-1"><Plane className="h-3 w-3" />Viaje / Cliente</Label>
            <Select value={tripId} onValueChange={setTripId}>
              <SelectTrigger className="h-9 w-[280px]" data-testid="sub-filter-trip"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Todos los viajes</SelectItem>
                {trips.map((t) => (
                  <SelectItem key={t.trip_id} value={t.trip_id}>{t.client_name} · {t.destination || "—"}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Según pago</Label>
            <Select value={pago} onValueChange={setPago}>
              <SelectTrigger className="h-9 w-[220px]" data-testid="sub-filter-pago"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Todas</SelectItem>
                {PAGO_ORDER.map((k) => <SelectItem key={k} value={k}>{PAGOS[k].label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="relative min-w-[180px] flex-1 space-y-1">
            <Label className="text-xs">Buscar</Label>
            <Search className="absolute left-2.5 top-[30px] h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cliente, servicio, proveedor…" className="h-9 pl-8" data-testid="sub-search" />
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="sub-summary">
        <Badge variant="secondary">{rows.length} reserva(s)</Badge>
        {pago !== ALL && <span className="text-xs text-muted-foreground">· filtro: <strong>{PAGOS[pago]?.label}</strong></span>}
      </div>

      {listQ.isLoading ? <Skeleton className="h-64 w-full" /> : rows.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-sm text-muted-foreground" data-testid="sub-empty">No hay reservas con los filtros aplicados.</CardContent></Card>
      ) : (
        <div className="overflow-x-auto rounded-xl border">
          <table className="w-full text-left text-sm">
            <thead className="bg-zinc-50 text-[10px] uppercase tracking-wide text-muted-foreground">
              <tr>
                {tripId === ALL && <th className="px-3 py-3">Cliente</th>}
                <th className="px-3 py-3">Servicio</th>
                <th className="px-3 py-3">Proveedor</th>
                <th className="px-3 py-3">Pago</th>
                <th className="px-3 py-3">Factura</th>
                <th className="px-3 py-3 text-center">PDF</th>
                <th className="px-3 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t align-middle hover:bg-zinc-50/50" data-testid={`sub-row-${r.id}`}>
                  {tripId === ALL && <td className="px-3 py-2.5"><div className="font-medium">{r.client_name}</div><div className="text-[11px] text-muted-foreground">{r.agent}</div></td>}
                  <td className="px-3 py-2.5">
                    <div className="font-medium">{r.service}</div>
                    <div className="text-[11px] text-muted-foreground">{[fmtDate(r.date_entry), r.city].filter(Boolean).join(" · ")}</div>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1.5"><Building2 className="h-3 w-3 text-muted-foreground" />{r.provider_name || "—"}</div>
                  </td>
                  <td className="px-3 py-2.5" data-testid={`sub-pago-${r.id}`}><PagoBadge pagada={r.pagada} /></td>
                  <td className="px-3 py-2.5" data-testid={`sub-factura-${r.id}`}><FacturaBadge r={r} /></td>
                  <td className="px-3 py-2.5 text-center" data-testid={`sub-pdf-${r.id}`}>
                    {r.n_facturas > 0 ? (
                      <Button size="sm" variant="outline" className="h-8" onClick={() => setOpenRow(r)} data-testid={`sub-ver-${r.id}`}>
                        <Eye className="mr-1 h-3.5 w-3.5" />Ver <Badge variant="secondary" className="ml-1 text-[10px]">{r.n_facturas}</Badge>
                      </Button>
                    ) : <span className="text-xs text-muted-foreground">—</span>}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <Button size="sm" onClick={() => setOpenRow(r)} data-testid={`sub-open-${r.id}`}>
                      <UploadCloud className="mr-1.5 h-3.5 w-3.5" />{r.n_facturas > 0 ? "Subir / ver" : "Subir / completar"}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openRow && (
        <SubirFacturaDialog row={openRow} onClose={() => setOpenRow(null)} onSaved={invalidate} />
      )}
    </div>
  );
}
